import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useId, useState } from "react";
import { useLocation, useParams } from "wouter";
import type { Tank } from "@/api/model/tank";
import ConfirmModal from "@/components/ConfirmModal";
import Button from "@/components/ui/Button";
import { ModalFooter } from "@/components/ui/ModalFooter";
import {
  appShell,
  createTankAtom,
  deleteTankAtom,
  nutrientsAtom,
  projectByIdAtom,
  projectTanksByTankIdAtom,
  selectedPropertyIdAtom,
  tanksByIdAtom,
  updateTankAtom,
} from "@/store";

interface TankFormData {
  label: string;
  nutrient: string;
  volume_liters: string;
  concentration_g_l: string;
  notes: string;
}

export function TankForm() {
  const [, setLocation] = useLocation();
  const { id } = useParams<{ id: string }>();
  const isEditing = id !== "new";

  const setBackButton = useSetAtom(appShell.backButtonAtom);
  useEffect(() => {
    setBackButton("/app/tanks");
    return () => {
      setBackButton(false);
    };
  }, [setBackButton]);

  const labelId = useId();
  const nutrientId = useId();
  const volumeLitersId = useId();
  const concentrationGLId = useId();
  const notesId = useId();

  const nutrients = useAtomValue(nutrientsAtom);
  const selectedPropertyId = useAtomValue(selectedPropertyIdAtom);
  const tankById = useAtomValue(tanksByIdAtom);
  const projectById = useAtomValue(projectByIdAtom);
  const projectTanksByTankId = useAtomValue(projectTanksByTankIdAtom);
  const createTank = useSetAtom(createTankAtom);
  const updateTank = useSetAtom(updateTankAtom);
  const deleteTank = useSetAtom(deleteTankAtom);

  const currentTank = isEditing && id ? tankById(id) : null;
  const activeProjectAssignments = isEditing && id ? projectTanksByTankId(id) : [];
  const projectNames = activeProjectAssignments.map((pt) => {
    const project = projectById(pt.project);
    return project?.name || pt.project;
  });

  const [formData, setFormData] = useState<TankFormData>({
    label: "",
    nutrient: "",
    volume_liters: "",
    concentration_g_l: "",
    notes: "",
  });

  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isEditing && id) {
      const tank = tankById(id);
      if (tank) {
        setFormData({
          label: tank.label || "",
          nutrient: tank.nutrient?.id || "",
          volume_liters: tank.volume_liters?.toString() || "",
          concentration_g_l: tank.concentration_g_l?.toString() || "",
          notes: tank.notes || "",
        });
      }
    } else if (!isEditing) {
      setFormData({
        label: "",
        nutrient: "",
        volume_liters: "",
        concentration_g_l: "",
        notes: "",
      });
    }
  }, [isEditing, id, tankById]);

  const handleInputChange =
    (field: keyof TankFormData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormData((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleSelectChange = (field: keyof TankFormData) => (event: React.ChangeEvent<HTMLSelectElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const tankData: Partial<Tank> = {
        property: selectedPropertyId!,
        label: formData.label,
        nutrient: formData.nutrient,
        volume_liters: parseFloat(formData.volume_liters),
        concentration_g_l: parseFloat(formData.concentration_g_l),
        notes: formData.notes || null,
      };

      if (isEditing && id) {
        await updateTank({ id, data: tankData });
      } else {
        await createTank(tankData);
      }

      setLocation("/app/tanks");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao salvar tanque");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setLocation("/app/tanks");
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteTank(id);
      setLocation("/app/tanks");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro ao excluir tanque");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <h1
        className={`text-2xl font-bold text-gray-900 ${isEditing && activeProjectAssignments.length > 0 ? "mb-1" : "mb-6"}`}
      >
        {isEditing ? "Editar Tanque" : "Novo Tanque"}
      </h1>
      {isEditing && activeProjectAssignments.length > 0 && (
        <span className="inline-flex items-center rounded-full bg-amber-50 px-2.5 py-0.5 text-xs font-medium text-amber-700 mb-5">
          Em uso por: {projectNames.join(", ")}
        </span>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">{error}</div>}

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label htmlFor={labelId} className="block text-sm font-medium text-gray-700 mb-2">
            Identificação *
          </label>
          <input
            type="text"
            id={labelId}
            value={formData.label}
            onChange={handleInputChange("label")}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Ex: Tanque A"
          />
          <p className="text-xs text-gray-500 mt-1">A identificação deve ser única nesta propriedade.</p>
        </div>

        <div>
          <label htmlFor={nutrientId} className="block text-sm font-medium text-gray-700 mb-2">
            Nutriente *
          </label>
          <select
            id={nutrientId}
            value={formData.nutrient}
            onChange={handleSelectChange("nutrient")}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
          >
            <option value="">Selecione um nutriente</option>
            {nutrients.map((nutrient) => (
              <option key={nutrient.id} value={nutrient.id}>
                {nutrient.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label htmlFor={volumeLitersId} className="block text-sm font-medium text-gray-700 mb-2">
            Volume (litros) *
          </label>
          <input
            type="number"
            id={volumeLitersId}
            value={formData.volume_liters}
            onChange={handleInputChange("volume_liters")}
            required
            min="0"
            step="0.1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Volume em litros"
          />
        </div>

        <div>
          <label htmlFor={concentrationGLId} className="block text-sm font-medium text-gray-700 mb-2">
            Concentração (g/L) *
          </label>
          <input
            type="number"
            id={concentrationGLId}
            value={formData.concentration_g_l}
            onChange={handleInputChange("concentration_g_l")}
            required
            min="0"
            step="0.1"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Concentração em gramas por litro"
          />
          {formData.concentration_g_l && formData.volume_liters && (
            <p className="text-sm text-gray-600 mt-2">
              Peso total do nutriente:{" "}
              <span className="font-semibold">
                {(parseFloat(formData.concentration_g_l) * parseFloat(formData.volume_liters)) / 1000} kg
              </span>
            </p>
          )}
        </div>

        <div>
          <label htmlFor={notesId} className="block text-sm font-medium text-gray-700 mb-2">
            Observações
          </label>
          <textarea
            id={notesId}
            value={formData.notes}
            onChange={handleInputChange("notes")}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Observações adicionais"
          />
        </div>

        <ModalFooter
          children={
            <>
              {isEditing && (
                <Button
                  type="button"
                  onClick={handleDelete}
                  loading={deleting}
                  disabled={loading}
                  variant="destructive"
                  size="md"
                  className="flex-1"
                >
                  Excluir
                </Button>
              )}
              <Button
                onClick={handleCancel}
                disabled={loading || deleting}
                variant="secondary"
                size="md"
                className="flex-1"
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={
                  deleting ||
                  !formData.label.trim() ||
                  !formData.nutrient ||
                  !formData.volume_liters ||
                  !formData.concentration_g_l
                }
                variant="primary"
                size="md"
                className="flex-1"
              >
                {isEditing ? "Atualizar" : "Criar"}
              </Button>
            </>
          }
        />
      </form>

      {/* Delete confirmation modal */}
      {isEditing && id && currentTank && (
        <ConfirmModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          title="Excluir Tanque"
          confirmText="Excluir"
          cancelText="Cancelar"
          variant="danger"
        >
          <div className="space-y-3">
            <p className="text-gray-700">
              Tem certeza que deseja excluir o tanque <strong>{currentTank.label}</strong>?
            </p>
            {activeProjectAssignments.length > 0 && (
              <div>
                <p className="text-sm text-gray-600 font-medium">Este tanque está em uso por:</p>
                <ul className="text-sm text-gray-600 mt-1 list-disc list-inside">
                  {projectNames.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
                <p className="text-xs text-amber-700 mt-2">
                  As associações do projeto e as receitas de fertirrigação vinculadas serão removidas.
                </p>
              </div>
            )}
            <p className="text-xs text-gray-500">Esta exclusão pode ser recuperada pela lixeira.</p>
          </div>
        </ConfirmModal>
      )}
    </div>
  );
}
